import React, { useState } from "react";
import { Badge, Checkbox, Group, List, Stack, Stepper, Text } from "@mantine/core";
import { Alert, Button, spacePx } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { CataloguePlan, PlanStep } from "@models/access";
import type { PlanError } from "@hooks/access/useCataloguePlan";
import { currentStep, stepResultText } from "./plans";

interface PlanStepperProps {
  plan: CataloguePlan;
  environment: string;
  busy: boolean;
  error: PlanError | null;
  /** Run step `n`; the deploy gate (step 4) passes its confirmation. */
  onRun: (n: number, confirmDeploy: boolean) => void;
}

const STATUS_COLOR: Record<PlanStep["status"], string> = {
  pending: "gray", done: "green", skipped: "teal", failed: "red",
};

/** Organisations of the licences in a PERMISSION_IN_LICENCE error (`licences[].organisations`). */
const licenceOrganisations = (value: unknown): unknown[] =>
  Array.isArray(value) ? value.flatMap((l) => (l as { organisations?: unknown[] })?.organisations ?? []) : [];

const namesOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((v) => (typeof v === "string" ? v : (v as { name?: string; id?: string })?.name ?? (v as { id?: string })?.id ?? ""))
        .filter(Boolean)
    : [];

/**
 * The steps of a catalogue plan (§7), run one per request, in order. Each step
 * shows its status and BeeHive's result; the current one has Run (or Retry
 * after a failure), and the deploy gate needs the environment ticked first.
 */
const PlanStepper: React.FC<PlanStepperProps> = ({ plan, environment, busy, error, onRun }) => {
  const { t } = useTranslation();
  const [gateTicked, setGateTicked] = useState(plan.deployConfirmation !== null);
  const next = currentStep(plan);
  const nextIndex = plan.steps.findIndex((s) => s.n === next);
  const active = nextIndex === -1 ? plan.steps.length : nextIndex;
  const running = plan.status === "open";

  return (
    <Stack gap="md">
      <Stepper active={active} orientation="vertical" size="sm" allowNextStepsSelect={false}>
        {plan.steps.map((step) => {
          const isCurrent = running && step.n === next;
          const isGate = step.operation === "deployGate";
          return (
            <Stepper.Step
              key={step.n}
              label={
                <Group gap="xs">
                  <Text size="sm" fw={600}>{t(`admin.access.permissions.steps.${step.operation}`)}</Text>
                  <Badge size="xs" color={STATUS_COLOR[step.status]} variant="light">
                    {t(`admin.access.permissions.stepStatus.${step.status}`)}
                  </Badge>
                </Group>
              }
              color={step.status === "failed" ? "red" : undefined}
              description={
                <Stack gap={spacePx[1]} mt={spacePx[1]} data-testid={`plan-step-${step.n}`}>
                  <Text size="xs" c="dimmed">{step.title}</Text>
                  {step.roles.length > 0 && (
                    <List size="xs" spacing={spacePx[1]}>
                      {step.roles.map((r) => (
                        <List.Item key={r.id}>
                          {t("admin.access.permissions.roleImpact", { role: r.name, count: r.assignedUsers })}
                        </List.Item>
                      ))}
                    </List>
                  )}
                  {stepResultText(step.result) && <Text size="xs" ff="monospace">{stepResultText(step.result)}</Text>}
                  {step.error && <Text size="xs" c="red">{step.error.message}</Text>}
                  {isCurrent && isGate && (
                    <Checkbox
                      size="xs"
                      checked={gateTicked}
                      onChange={(e) => setGateTicked(e.currentTarget.checked)}
                      label={t(plan.kind === "delete"
                        ? "admin.access.permissions.gateDelete"
                        : "admin.access.permissions.gate", { environment })}
                    />
                  )}
                  {isCurrent && (
                    <Group>
                      <Button
                        size="xs"
                        loading={busy}
                        disabled={isGate && !gateTicked}
                        onClick={() => onRun(step.n, isGate && gateTicked)}
                      >
                        {step.status === "failed" ? t("admin.access.permissions.retry") : t("admin.access.permissions.run")}
                      </Button>
                    </Group>
                  )}
                </Stack>
              }
            />
          );
        })}
      </Stepper>

      {error && (
        <Alert color="red" title={error.code ?? undefined}>
          <Stack gap="xs">
            <Text size="sm">
              {error.code === "PERMISSION_IN_USE" ? t("admin.access.permissions.inUse")
                : error.code === "PERMISSION_IN_LICENCE" ? t("admin.access.permissions.inLicence")
                : error.message}
            </Text>
            {namesOf(error.detail?.roles).length > 0 && (
              <Text size="xs">{t("admin.access.permissions.heldBy", { roles: namesOf(error.detail?.roles).join(", ") })}</Text>
            )}
            {namesOf(licenceOrganisations(error.detail?.licences)).length > 0 && (
              <Text size="xs">
                {t("admin.access.permissions.licencedBy", {
                  organisations: namesOf(licenceOrganisations(error.detail?.licences)).join(", "),
                })}
              </Text>
            )}
            {error.code === "PERMISSION_IN_USE" && plan.steps.some((s) => s.n === 5) && (
              <Group>
                <Button size="xs" variant="light" loading={busy} onClick={() => onRun(5, false)}>
                  {t("admin.access.permissions.backToStep", { n: 5 })}
                </Button>
              </Group>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

export default PlanStepper;
